/**
 * Element ResizeObserver Module
 * Generic module for observing element size changes and notifying .NET callbacks
 * 
 * This module provides a reusable ResizeObserver implementation that can be used
 * across different components that need to respond to container size changes.
 */

import type { DotNetObjectReference } from '../types';

/**
 * Represents an active ResizeObserver instance for an element
 */
interface ElementResizeObserverInstance {
    observer: ResizeObserver;
    callback: DotNetObjectReference;
    element: HTMLElement;
}

/**
 * Map of active observers keyed by element
 */
const activeObservers: Map<HTMLElement, ElementResizeObserverInstance> = new Map();

/**
 * Observes resize events for an element and notifies .NET
 * 
 * @param element - The DOM element to observe
 * @param dotNetHelper - .NET object reference for callback invocation
 * @param callbackMethodName - Name of the .NET method to invoke (default: 'OnContainerResized')
 */
export function observeResize(
    element: HTMLElement, 
    dotNetHelper: DotNetObjectReference, 
    callbackMethodName: string = 'OnContainerResized'
): void {
    if (!element || !dotNetHelper) {
        console.warn('ElementResizeObserver: Invalid element or callback provided');
        return;
    }

    // Disconnect any existing observer for this element
    disconnectElement(element);

    // Create a new ResizeObserver
    const observer = new ResizeObserver((entries: ResizeObserverEntry[]) => {
        for (const entry of entries) {
            // Get the content box width (excludes padding and border)
            const width = Math.round(entry.contentRect.width);
            
            // Call back to .NET with the new width
            try {
                dotNetHelper.invokeMethodAsync<void>(callbackMethodName, width);
            } catch (error) {
                console.error('ElementResizeObserver: Error invoking .NET callback', error);
            }
        }
    });

    // Store the observer instance
    activeObservers.set(element, {
        observer,
        callback: dotNetHelper,
        element
    });

    // Start observing the element
    observer.observe(element);

    // Get and report initial size immediately
    reportInitialSize(element, dotNetHelper, callbackMethodName);
}

/**
 * Reports the initial size of an element to .NET
 * 
 * @param element - The element to measure
 * @param callback - .NET callback reference
 * @param callbackMethodName - Name of the .NET method to invoke
 */
function reportInitialSize(
    element: HTMLElement, 
    callback: DotNetObjectReference, 
    callbackMethodName: string
): void {
    try {
        const rect = element.getBoundingClientRect();
        const width = Math.round(rect.width);
        
        callback.invokeMethodAsync<void>(callbackMethodName, width);
    } catch (error) {
        console.error('ElementResizeObserver: Error reporting initial size', error);
    }
}

/**
 * Disconnects the ResizeObserver for a specific element
 * 
 * @param element - The element whose observer should be disconnected
 */
function disconnectElement(element: HTMLElement): void {
    const instance = activeObservers.get(element);
    
    if (instance) {
        instance.observer.disconnect();
        activeObservers.delete(element);
    }
}

/**
 * Disconnects all active ResizeObservers
 * This is called when the module is being disposed
 */
export function disconnect(): void {
    activeObservers.forEach((instance) => {
        instance.observer.disconnect();
    });
    
    activeObservers.clear();
}

/**
 * Gets the number of active observers (for debugging)
 * 
 * @returns The count of active ResizeObserver instances
 */
export function getActiveObserverCount(): number {
    return activeObservers.size;
}

// Export the module interface
const ElementResizeObserverModule = {
    observeResize,
    disconnect,
    getActiveObserverCount
};

export default ElementResizeObserverModule;
